import React from 'react'
import Table from '../../../../components/common/Table/Table'
import Datepicker from '../../../../components/common/DatePicker/Datepicker'
import { TABLE_COLUMNS } from './constants'
import { useOtherRequestDetails } from './useOtherRequestDetails'

type TableColumn = {
  key: string
  label: string
}

interface CurrentRequestSectionProps {
  isDraft?: boolean
  tableRows: any[]
  highlightedDates: string[]
  committedDates?: string[]
  /**
   * When provided, the calendar becomes interactive (not read-only) and this
   * callback is fired with the clicked date string (MM/DD/YYYY format) so the
   * parent can open the Request Time Off form for that date.
   */
  onDateClick?: (date: string) => void
  /**
   * Optional content to render to the right of the calendar (e.g. a time-off
   * request form), mirroring the CreateNewRequest split layout.
   */
  rightPanel?: React.ReactNode
  /**
   * When true, on-hold (SUBMITTED_ONHOLD) dates remain clickable so the parent
   * can open them for editing. Defaults to false (all other-request dates are
   * blocked).
   */
  canEnterEditMode?: boolean
  /** Override the calendar popup width in pixels. */
  calendarWidth?: number
  /** Override the calendar popup height in pixels. */
  calendarHeight?: number
  /** Optional column override for table actions (e.g. edit/remove). */
  tableColumns?: TableColumn[]
  /** Controls whether the calendar panel is rendered. */
  showCalendar?: boolean
  /** Enables vacation-bid on-hold styling for vacation-bid request screens only. */
  highlightVacationBidOnHold?: boolean
  /** Optional custom cell renderer, e.g. to render a checkbox for vacation-bid "Day in Other Requests" rows. */
  renderCell?: (row: any, column: TableColumn) => React.ReactNode

  selectedDatesForCalendar?: string[]
  isEditMode?: boolean
  editDate?: string | null
  editModeSelectedDates?: string[]
  disableCommittedDateClick?: boolean
  /** Committed dates that stay clickable even when disableCommittedDateClick is true (e.g. a Partial Day date with unused hours remaining). */
  unblockedCommittedDates?: string[]
  onAddMoreDates?: () => void
  /**
   * The request currently being viewed/edited — excluded from its own
   * "other requests" data so a date that still belongs to this same
   * (unsaved) request is never misclassified as another request's date.
   * Without this, a date removed client-side from the request stays green
   * and unselectable in the calendar until the page reloads.
   */
  excludeRequestId?: string | number | null
}

const CurrentRequestSection: React.FC<CurrentRequestSectionProps> = ({
  isDraft,
  tableRows,
  highlightedDates,
  committedDates,
  onDateClick,
  rightPanel,
  canEnterEditMode = false,
  calendarWidth = 372,
  calendarHeight = 399,
  tableColumns = TABLE_COLUMNS,
  showCalendar = true,
  highlightVacationBidOnHold = false,
  renderCell,
  selectedDatesForCalendar,
  isEditMode,
  editDate,
  editModeSelectedDates,
  onAddMoreDates,
  disableCommittedDateClick = false,
  unblockedCommittedDates,
  excludeRequestId,
}) => {
  const {
    otherRequestDates,
    otherRequestDetailsForTooltip,
    handleMonthYearChange,
  } = useOtherRequestDetails(
    selectedDatesForCalendar ?? highlightedDates,
    true,
    excludeRequestId
  )
  const hasCalendarPanel = showCalendar && highlightedDates.length > 0
  let sectionMarginClass = ''
  if (hasCalendarPanel) {
    sectionMarginClass = 'mt-12!'
  } else if (rightPanel) {
    sectionMarginClass = 'mt-8!'
  }

  if (tableRows.length === 0) return null

  return (
    <div className="w-full mt-6">
      <h2 className="h2">Current Request</h2>
      <div className="mt-4">
        {isDraft && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="button -secondary block w-full! desktop:w-auto!"
            onClick={onAddMoreDates}
          >
            Add date to request
          </button>
        </div>
        )}
        <Table columns={tableColumns} rows={tableRows} renderCell={renderCell} caption="" />
      </div>
      <div className={sectionMarginClass}>
        {hasCalendarPanel && (
          <p className="p3 -book">
            {onDateClick
              ? 'Click a date below to add date in your time off'
              : 'View request details by clicking a specific date in the request.'}
          </p>
        )}

        {(hasCalendarPanel || rightPanel) && (
          <div className="flex gap-16! tablet:gap-8! mobile:gap-2! mt-4 items-start flex-col tablet:flex-row mobile:flex-col">
            <div className={hasCalendarPanel ? 'w-full lg:w-auto' : 'hidden'}>
              {hasCalendarPanel && (
                <Datepicker
                  name="dates"
                  label="Select Dates"
                  openBehaviour="calendarView"
                  value={selectedDatesForCalendar ?? highlightedDates}
                  datesToBeHighlighted={highlightedDates}
                  tableRows={tableRows}
                  readOnly={!onDateClick}
                  onDateClick={onDateClick}
                  onMonthYearChange={handleMonthYearChange}
                  isEditMode={isEditMode}
                  editDate={editDate ?? selectedDatesForCalendar?.[0]}
                  editModeSelectedDates={
                    editModeSelectedDates ??
                    selectedDatesForCalendar ??
                    highlightedDates
                  }
                  otherSelectedDatesToShow={otherRequestDates}
                  otherRequestDetails={otherRequestDetailsForTooltip}
                  canEnterEditMode={canEnterEditMode}
                  committedDates={committedDates}
                  disableCommittedDateClick={disableCommittedDateClick}
                  unblockedCommittedDates={unblockedCommittedDates}
                  highlightVacationBidOnHold={highlightVacationBidOnHold}
                  calendarWidth={calendarWidth}
                  calendarHeight={calendarHeight}
                />
              )}
            </div>

            {rightPanel && <div className="w-full lg:flex-1">{rightPanel}</div>}
          </div>
        )}
      </div>
    </div>
  )
}

export default CurrentRequestSection
